import { useState } from 'react'
import { AI_LABEL_TEXT, aiLabelGeometry, aiLabelOffset } from '@backend/lib/util/aiLabelSpec'
import { buildAiLabelSvg } from '@backend/lib/util/aiLabelSvg'
import { DEFAULT_AI_LABEL_STYLE, type AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'
import { cn } from '@/lib/utils'

/**
 * Draws the visible AI-disclosure label over a previewed image, using the same
 * geometry module the publish-time baker uses.
 *
 * This exists because the preview was silently dishonest: the modal forwarded
 * `bakeAiLabel` into the publish settings but never rendered anything, so a user
 * approved a clean image and published one with a pill burned into the pixels.
 * A preview that omits a permanent, non-undoable change to the artwork is worse
 * than no preview.
 *
 * Geometry is resolved from the image's NATURAL pixel size, not its displayed
 * size, because `aiLabelGeometry` clamps the font size in absolute pixels. Sizing
 * off the rendered element would drift from the baked result on any image whose
 * short side falls near a clamp. Every offset is then emitted as a percentage of
 * the natural dimensions, so the overlay tracks the image at whatever size the
 * preview happens to render it.
 */
export const AiLabelOverlay = ({
  naturalWidth,
  naturalHeight,
  style,
}: {
  naturalWidth: number
  naturalHeight: number
  style?: AiLabelStyle
}) => {
  const resolved = style ?? DEFAULT_AI_LABEL_STYLE
  const text = resolved.text.trim() || AI_LABEL_TEXT
  const geometry = aiLabelGeometry(naturalWidth, naturalHeight, text, resolved.scale)
  const svg = buildAiLabelSvg(text, geometry, {
    backgroundColor: resolved.backgroundColor,
    textColor: resolved.textColor,
    backgroundAlpha: resolved.backgroundAlpha,
  })

  // A data URI rather than inline markup: the SVG is scaled by the browser the
  // same way the rasterised PNG is scaled into the post, and it keeps us clear
  // of injecting raw HTML for something that flows through a settings field.
  const source = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`

  // Same offset calculation the baker composites with, so the two cannot drift.
  const { x, y } = aiLabelOffset(naturalWidth, naturalHeight, geometry, resolved.position)

  return (
    <img
      src={source}
      alt=""
      aria-hidden
      className="pointer-events-none absolute select-none"
      style={{
        left: `${(x / naturalWidth) * 100}%`,
        top: `${(y / naturalHeight) * 100}%`,
        width: `${(geometry.width / naturalWidth) * 100}%`,
      }}
    />
  )
}

type AiLabeledImageProps = {
  src: string | undefined
  className?: string
  /**
   * Sizing for the positioning wrapper, which sits between the image and its
   * old parent. Leave unset for an `object-contain` image so the wrapper
   * shrink-wraps the rendered picture and the label tracks the image rather than
   * the letterboxed container. Pass the image's own sizing (`h-full w-full`)
   * when it fills its parent via `object-cover`.
   */
  wrapperClassName?: string
  /** Whether this post will actually be published with a baked label. */
  showAiLabel?: boolean
  /** Appearance of that label, so the preview reflects the user's settings
   *  rather than the defaults. */
  aiLabelStyle?: AiLabelStyle
  alt?: string
  onClick?: () => void
  /** Chained, not replaced — several previews already measure the image on load
   *  to decide aspect-ratio cropping. */
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void
  crossOrigin?: 'anonymous' | 'use-credentials'
}

/**
 * Drop-in replacement for a preview `<img>` that can carry the AI label.
 *
 * The label only appears once the image has loaded, because its geometry depends
 * on the natural dimensions. That is a deliberate trade: a late-but-correct label
 * beats an immediate one drawn at the wrong scale.
 */
export const AiLabeledImage = ({
  src,
  className,
  wrapperClassName,
  showAiLabel = false,
  aiLabelStyle,
  alt = '',
  onClick,
  onLoad,
  crossOrigin,
}: AiLabeledImageProps) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)

  return (
    <div className={cn('relative', wrapperClassName ?? 'inline-block')}>
      <img
        src={src}
        alt={alt}
        className={className}
        onClick={onClick}
        crossOrigin={crossOrigin}
        onLoad={(event) => {
          setSize({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
          onLoad?.(event)
        }}
      />
      {showAiLabel && size && size.width > 0 && size.height > 0 && (
        <AiLabelOverlay
          naturalWidth={size.width}
          naturalHeight={size.height}
          style={aiLabelStyle}
        />
      )}
    </div>
  )
}
